import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect, shallowEqual } from 'react-redux';
import { logger } from 'client/utils/logger/logger';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { ShowError } from 'client/site-modules/shared/components/show-error/show-error';

const MAX_COMPONENT_TRACE_LENGTH = 4000; // slightly less than 4KB to be safe

function fireEdwPixel({ message, error, pageName, creativeId }) {
  const errorMessage = error?.message ? `Error: ${error.message}` : '';
  const value = `${message} ${errorMessage}`.trim();
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_SHOW_CONTENT,
      subaction_name: TrackingConstant.MINOR_BREAKDOWN_ERROR,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      creative_id: creativeId,
      value,
      url: window?.location?.href ?? '',
      page_name: pageName,
    },
  });
}

/**
 * this was added to work around a newrelic max length limitation
 */
function getTruncatedErrorInfo(info) {
  const truncatedInfo = { ...info };
  truncatedInfo.componentStack =
    truncatedInfo.componentStack.length > MAX_COMPONENT_TRACE_LENGTH
      ? `${truncatedInfo.componentStack.substring(0, MAX_COMPONENT_TRACE_LENGTH)}... [truncated]`
      : truncatedInfo.componentStack;
  truncatedInfo.react_error_boundary = 'true';
  return truncatedInfo;
}

function getLoggerMessage(message) {
  return `[ErrorBoundary] ${message}`.trim();
}

function log(level, { pageName, creativeId, message, endUserMessage, error, info }) {
  const truncatedInfo = getTruncatedErrorInfo(info);
  const loggerMessage = getLoggerMessage(message || endUserMessage);
  logger[level](error, loggerMessage, truncatedInfo);
  if (level === 'error') {
    fireEdwPixel({ message: loggerMessage, error, info: truncatedInfo, pageName, creativeId });
  }
}

function shouldResetErrorState(prevResetProps, resetProps) {
  return !resetProps ? false : !shallowEqual(prevResetProps, resetProps);
}

const INITIAL_STATE = { hasError: false, error: null, info: null };

class ErrorBoundaryUI extends Component {
  static propTypes = {
    creativeId: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
    Fallback: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
    message: PropTypes.string,
    endUserMessage: PropTypes.string,
    pageName: PropTypes.string.isRequired,
    resetProps: PropTypes.shape({}),
  };

  static defaultProps = {
    creativeId: 'not-provided',
    Fallback: ShowError,
    message: '',
    endUserMessage: '',
    resetProps: null,
  };

  state = INITIAL_STATE;

  timeoutId = null;

  componentWillUnmount() {
    if (this.timeoutId) {
      const { creativeId } = this.props;
      logger.debug(`[ErrorBoundary] Unmounting. error from ${creativeId} will not be logged`);
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  componentDidCatch(error, info) {
    this.setState({ hasError: true, error, info });

    // next, log it to New Relic/edw in a way that differentiates unrecovered errors from recovered ones...
    const { pageName, creativeId, message, endUserMessage, resetProps } = this.props;
    if (!resetProps) {
      // no chance of recovery - classify as error
      log('error', { pageName, creativeId, message, endUserMessage, error, info });
      return;
    }
    // only warn for now as it may recover thus limiting impact to simply having no loading state - does not warrant an error
    log('warn', { pageName, creativeId, message, endUserMessage, error, info });

    this.timeoutId = setTimeout(() => {
      this.timeoutId = null;
      if (!this.state.hasError) {
        return; // recovered - no need to log
      }
      // this is the error from the 1) first render after last reset, or 2) very first render if there were no resets
      log('error', { pageName, creativeId, message, endUserMessage, error, info });
    }, 2000);
  }

  componentDidUpdate(prevProps) {
    const prevResetProps = prevProps.resetProps;
    const { resetProps, creativeId } = this.props;
    if (this.state.hasError && shouldResetErrorState(prevResetProps, resetProps)) {
      logger.debug(
        { prevResetProps, resetProps },
        `[ErrorBoundary] Resetting error state for ${creativeId} due to change to one or more prop`
      );
      this.resetBoundary();
    }
  }

  resetBoundary = () => {
    if (this.timeoutId) {
      // no error will be logged unless error happens again
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.setState(INITIAL_STATE);
  };

  render() {
    const { hasError, error, info } = this.state;
    const { Fallback, message, endUserMessage } = this.props;

    if (!hasError) return this.props.children;

    const loggerMessage = getLoggerMessage(message);
    return <Fallback error={error} info={info} message={loggerMessage} endUserMessage={endUserMessage} />;
  }
}

const mapStateToProps = state => ({
  pageName: state?.pageContext?.page?.name ?? '',
});

export const ErrorBoundary = connect(mapStateToProps)(ErrorBoundaryUI);
